import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChecklistDownload } from "@/components/ChecklistDownload";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { resources } from "@/data/content";

const Resources = () => (
  <>
    <SEO
      title="Resources | Bello Joy Consulting"
      description="Insurance education, family protection guidance, financial wisdom, and stewardship resources from Bello Joy Consulting."
    />
    <PageHero
      testId="resources-page-hero"
      kicker="Blog & Resources"
      title="Wisdom for protecting"
      accent="what matters most."
      description="Educational reflections and practical guidance on insurance, family protection, financial stewardship, business continuity, and legacy planning."
    />

    <section data-testid="resources-list-section" className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <ChecklistDownload />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
          {resources.map((article, index) => {
            const Icon = article.icon;
            return (
              <Reveal key={article.title} delay={index * 0.06} data-testid={`resource-card-${index + 1}`}>
                <Link to={`/resources/${article.slug}`} data-testid={`resource-article-link-${article.slug}`} className="group block h-full">
                  <article className="flex h-full flex-col rounded-[2rem] border border-border bg-card p-7 shadow-soft transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-lift">
                    <div className="mb-8 flex items-center justify-between">
                      <span className="rounded-full bg-muted px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary/70">
                        {article.category}
                      </span>
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary/20 text-primary transition-colors duration-300 group-hover:bg-secondary/40">
                        <Icon size={20} />
                      </span>
                    </div>
                    <h2 className="text-2xl leading-snug tracking-tight text-primary">{article.title}</h2>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{article.excerpt}</p>
                    <div className="mt-7 flex items-center justify-between gap-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary">{article.readTime}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        Read article <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </article>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-16 rounded-[2rem] bg-primary p-8 text-primary-foreground shadow-soft md:p-12 dark-grain relative overflow-hidden" data-testid="resources-cta">
          <div className="relative z-10 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-secondary">Have a question?</p>
              <h2 className="text-3xl tracking-tight md:text-5xl">Get guidance shaped around your life.</h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/72">
                Articles can educate, but a personal conversation can bring clarity. Alicia is ready to listen.
              </p>
            </div>
            <Button asChild size="lg" className="rounded-full bg-secondary px-8 text-primary transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-secondary/90">
              <Link to="/contact" data-testid="resources-contact-button">Ask Alicia a Question <ArrowRight size={17} /></Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Resources;
